import React from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Divider, Tooltip,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  Lock as LockIcon, Star as StarIcon,
  MoreVert as MoreIcon, ArrowBackIosNew as BackIcon,
  ArrowForwardIos as FwdIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';

/**
 * Extension v5 — Toolbar icon
 *
 * End-user surface. A facility Chromebook user sees the AchieveDXP
 * icon pinned to the Chrome toolbar. Two states:
 *  - Normal (connected, no badge)
 *  - Error (disconnected — red dot overlay)
 *
 * This is the ENTIRE visible chrome most of the time. Scripts run
 * invisibly in the background; the icon exists only so users have a
 * trust anchor ("AchieveDXP is on and healthy") and a click-through to
 * the status popup.
 *
 * Rendered at arbitrary zoom (approx 5x) so the 16x16 icon is legible
 * for design review.
 */

const TEAL = '#1ebea5';
const DEEP_TEAL = '#0b847f';
const ZOOM = 5;   // every "px" below is rendered as ZOOM * 1px on the page

const AchieveDxpToolbarIcon = ({ error = false, size = 16 }) => {
  // AchieveDXP "A" glyph in a small rounded square, matching the
  // placeholder AchieveDxpLogo component used elsewhere in the mocks.
  // At 16x16 Chrome will antialias this heavily; we render a cleaner
  // vector so reviewers can eval the silhouette.
  const id = error ? 'err-bg' : 'ok-bg';
  return (
    <Box sx={{
      width: size * ZOOM, height: size * ZOOM, position: 'relative',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width={size * ZOOM} height={size * ZOOM} viewBox="0 0 16 16">
        <defs>
          <linearGradient id={id} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor={TEAL} />
            <stop offset="100%" stopColor={DEEP_TEAL} />
          </linearGradient>
        </defs>
        {/* Rounded square base */}
        <rect x="1" y="1" width="14" height="14" rx="3" fill={`url(#${id})`} />
        {/* "A" glyph for AchieveDXP wordmark */}
        <path d="M 5 12 L 8 4 L 11 12 M 6.2 9.5 L 9.8 9.5"
              stroke="#fff" strokeWidth="1.2" fill="none"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* Error red-dot overlay (bottom-right) */}
      {error && (
        <Box sx={{
          position: 'absolute',
          bottom: 1 * ZOOM, right: 1 * ZOOM,
          width: 5 * ZOOM, height: 5 * ZOOM,
          borderRadius: '50%',
          bgcolor: '#e53935',
          border: `${0.6 * ZOOM}px solid #fff`,
          boxShadow: '0 0 2px rgba(0,0,0,0.4)',
        }} />
      )}
    </Box>
  );
};

/**
 * A faux Chrome toolbar at ~1.5x zoom with the AchieveDXP icon slotted in
 * at the pinned-extension location. Gives reviewers context.
 */
const ToolbarFrame = ({ error = false, label }) => (
  <Box>
    <Typography variant="caption" sx={{
      display: 'block', textAlign: 'center', mb: 1,
      color: '#607d8b', fontWeight: 700, fontSize: '0.75rem',
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      {label}
    </Typography>
    <Paper elevation={2} sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 1.25, py: 0.75, borderRadius: 1.5,
      bgcolor: '#f1f3f4', border: '1px solid #dadce0',
    }}>
      <BackIcon sx={{ fontSize: 18, color: '#5f6368' }} />
      <FwdIcon sx={{ fontSize: 14, color: '#9aa0a6' }} />
      <RefreshIcon sx={{ fontSize: 18, color: '#5f6368' }} />
      <Box sx={{
        flexGrow: 1, mx: 0.75, px: 1.25, py: 0.5,
        bgcolor: '#fff', borderRadius: 3,
        display: 'flex', alignItems: 'center', gap: 0.75,
        border: '1px solid #dadce0', minWidth: 200,
      }}>
        <LockIcon sx={{ fontSize: 14, color: '#5f6368' }} />
        <Typography sx={{
          fontSize: '0.78rem', color: '#202124',
          fontFamily: '-apple-system, system-ui, sans-serif', flexGrow: 1,
        }}>
          {error ? 'achievedxp.nucleos.com (no connection)' : 'khanacademy.org'}
        </Typography>
        <StarIcon sx={{ fontSize: 14, color: '#5f6368' }} />
      </Box>
      <ExtensionIcon sx={{ fontSize: 18, color: '#5f6368' }} />
      {/* The AchieveDXP pinned icon — rendered at real toolbar size */}
      <Tooltip title={error
        ? 'AchieveDXP · disconnected (scripts may be stale)'
        : 'AchieveDXP · connected'}>
        <Box sx={{
          p: 0.25, borderRadius: 0.5,
          '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
          cursor: 'pointer',
        }}>
          <AchieveDxpToolbarIcon error={error} size={16} />
        </Box>
      </Tooltip>
      <MoreIcon sx={{ fontSize: 18, color: '#5f6368' }} />
    </Paper>
  </Box>
);

/**
 * Large "zoomed" preview of the 16x16 icon in both states so the design
 * can be scrutinized.
 */
const IconCloseUp = ({ error, caption }) => (
  <Stack alignItems="center" spacing={1}>
    <Box sx={{
      p: 3, bgcolor: '#fff', border: '1px dashed #b0bec5',
      borderRadius: 2, display: 'flex', alignItems: 'center',
      justifyContent: 'center',
    }}>
      <AchieveDxpToolbarIcon error={error} size={16} />
    </Box>
    <Typography variant="caption" sx={{ fontSize: '0.78rem', color: '#37474f', textAlign: 'center', maxWidth: 200 }}>
      {caption}
    </Typography>
  </Stack>
);

const ExtV5ToolbarIconMockup = () => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 4, p: 4, bgcolor: '#eef1f5',
    minHeight: 'calc(100vh - 180px)',
  }}>
    <Box sx={{ textAlign: 'center', maxWidth: 700 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
        <ExtensionIcon sx={{ fontSize: 24, color: TEAL }} />
        <Typography variant="h6" fontWeight={700}>
          Toolbar icon — the entire visible surface (most of the time)
        </Typography>
        <Chip label="END USER" size="small" sx={{ fontWeight: 700, bgcolor: '#e8f5e9', color: '#1b5e20' }} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        On a facility Chromebook, an inmate sees <b>nothing</b> from the AchieveDXP
        extension except this 16×16 icon pinned to the Chrome toolbar. Clicking it
        opens a small status popup (see <code>PopupMockup</code>). Everything else
        happens invisibly: scripts autoplay as the user navigates, content is filtered
        silently, progress is collected in the background.
      </Typography>
    </Box>

    <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ width: '100%', maxWidth: 1100 }}>
      <Box sx={{ flex: 1 }}>
        <ToolbarFrame error={false} label="Normal · connected" />
      </Box>
      <Box sx={{ flex: 1 }}>
        <ToolbarFrame error={true} label="Error · disconnected" />
      </Box>
    </Stack>

    <Divider sx={{ width: '80%', my: 1 }} />

    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle2" sx={{ mb: 2, color: '#455a64' }}>
        Close-up at print resolution (16×16 actual, rendered at {ZOOM}× zoom)
      </Typography>
      <Stack direction="row" spacing={6} justifyContent="center" alignItems="flex-start">
        <IconCloseUp error={false} caption="Normal. Teal gradient base, white A glyph. No badge." />
        <IconCloseUp error={true}  caption="Disconnected. Red dot in the bottom-right corner (visible at 16×16 since it is ~5×5)." />
      </Stack>
    </Box>

    <Paper elevation={0} sx={{
      maxWidth: 700, p: 2, bgcolor: '#fff3cd',
      border: '1px solid #ffe082', borderRadius: 1.5,
    }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#5d4037', fontSize: '0.75rem' }}>
        DESIGN NOTE
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#4e342e', mt: 0.5 }}>
        The current AchieveDXP extension uses a silent puzzle-piece icon with no
        popup. v5 adds the popup (for trust + self-service "is it working?") but
        does NOT add a sidebar for end users. The sidebar / authoring UX lives
        either in the Automatrix web dashboard or in a separate staff-facing
        extension installed on developer machines only.
      </Typography>
    </Paper>
  </Box>
);

export default ExtV5ToolbarIconMockup;
